import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchUserBookings, cancelBooking as apiCancelBooking } from "../services/api";
import { useNavigate } from "react-router-dom";
import "./EnhancedUserDashboard.css";
import { format } from 'date-fns';
import axios from "axios";

const EnhancedUserDashboard = () => {
  const { currentUser, isAgencyMember, hasPendingAgencyRequest, refreshUser, setCurrentUser, updateUser } = useAuth();
  const navigate = useNavigate();
  
  // State variables
  const [activeTab, setActiveTab] = useState("dashboard");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [profileData, setProfileData] = useState({
    name: currentUser?.name || "",
    email: currentUser?.email || "",
    phone: currentUser?.phone || "",
    address: currentUser?.address || "",
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [tabsVisible, setTabsVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  
  // Check local storage for agency status update
  const [agencyStatus, setAgencyStatus] = useState(currentUser?.agencyStatus || 'none');
  const [agencyName, setAgencyName] = useState(currentUser?.agencyName || '');
  const [agencyPosition, setAgencyPosition] = useState(currentUser?.position || 'Member');
  const [lastRefresh, setLastRefresh] = useState(null);

  // Update profileData when currentUser changes
  useEffect(() => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || "",
        email: currentUser.email || "",
        phone: currentUser.phone || "",
        address: currentUser.address || "",
      });
    }
  }, [currentUser]);

  // Function to check for agency status updates
  const checkAgencyStatus = useCallback(() => {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        if (userData.agencyId) {
          const newAgencyStatus = userData.agencyStatus || 'unknown';
          
          // Only show approval notification if status changed from something else to approved
          if (agencyStatus !== newAgencyStatus && newAgencyStatus === 'approved' && agencyStatus !== null) {
            setSuccessMessage("Your agency membership has been approved! You can now make bookings through your agency.");
            setTimeout(() => setSuccessMessage(''), 10000); // Show for 10 seconds
            
            // Refresh the user data in the auth context
            refreshUser();
          }
          
          setAgencyStatus(newAgencyStatus);
          setAgencyName(userData.agencyName || 'Unknown Agency');
          setAgencyPosition(userData.position || 'Member');
          
          // If the agency status is approved but not reflected in currentUser,
          // we should synchronize with our component state
          if (userData.agencyStatus === 'approved' && typeof isAgencyMember === 'function' && !isAgencyMember()) {
            console.log('Agency membership approved in localStorage but not in auth context');
            refreshUser();
          }
        }
      }
    } catch (err) {
      console.error('Error checking agency status:', err);
    }
  }, [agencyStatus, isAgencyMember, refreshUser]);

  // Set up a timer to check for agency status updates - using longer interval to avoid frequent checks
  useEffect(() => {
    // Check immediately on mount
    checkAgencyStatus();
    
    // Then set up an interval to check periodically - changed from 15s to 60s
    const intervalId = setInterval(checkAgencyStatus, 60000); // Check every 60 seconds
    
    return () => clearInterval(intervalId);
  }, [checkAgencyStatus]);

  // Function to format dates consistently
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Function to format time consistently
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch (error) {
      return "Invalid Time";
    }
  };

  // Load user's bookings
  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      // Update last refresh time
      const now = new Date();
      setLastRefresh(now.toLocaleTimeString());
      
      // First ensure we have the current user data
      let userId;
      
      try {
        // Get user data from localStorage
        const userJson = localStorage.getItem('user');
        if (!userJson) {
          console.error("No user data found in localStorage");
          setError("User data not found. Please log in again.");
          setLoading(false);
          return;
        }
        
        const userData = JSON.parse(userJson);
        userId = userData._id;
        
        // Double-check with sessionStorage for consistency
        const sessionUser = sessionStorage.getItem("currentSessionUser");
        
        if (sessionUser && sessionUser !== userId) {
          // Trust sessionStorage in this case (it's browser tab specific)
          if (sessionUser) {
            console.log("Using sessionStorage user ID instead:", sessionUser);
            userId = sessionUser;

            // Update the userData in localStorage to match sessionStorage
            userData._id = sessionUser;
            localStorage.setItem("user", JSON.stringify(userData));
            console.log("Updated localStorage user to match sessionStorage");
          }
        }
        
        console.log("EnhancedUserDashboard: Fetching bookings for user ID:", userId);
        
        // Store user ID in sessionStorage for consistency
        if (!sessionStorage.getItem("currentSessionUser")) {
          sessionStorage.setItem("currentSessionUser", userId);
        }
        
        // Check for agency membership approval messages
        if (userData.agencyStatus === 'approved' && userData.agencyStatusDate) {
          const approvalDate = new Date(userData.agencyStatusDate);
          const now = new Date();
          const millisecondsSinceApproval = now - approvalDate;
          const hoursSinceApproval = millisecondsSinceApproval / (1000 * 60 * 60);
          
          // If approved within the last 2 hours, show a welcome message
          if (hoursSinceApproval < 2) {
            setSuccessMessage(`Welcome to ${userData.agencyName || 'your agency'}! Your membership has been approved and you can now make bookings.`);
            setTimeout(() => setSuccessMessage(''), 8000);
          }
        }
      } catch (parseError) {
        console.error("Error parsing user data from localStorage:", parseError);
        setError("Error accessing user data. Please log in again.");
        setLoading(false);
        return;
      }
      
      // Fetch user bookings with error handling and fallbacks
      let bookingsData = [];
      
      try {
        // First try the normal API call
        bookingsData = await fetchUserBookings();
        console.log("Server returned bookings:", bookingsData.length);
        
        // Cache the successful response
        try {
          localStorage.setItem("enhancedUserBookings", JSON.stringify(bookingsData));
          console.log("Cached bookings in localStorage for future use");
        } catch (cacheError) {
          console.warn("Failed to cache bookings in localStorage:", cacheError);
        }
      } catch (apiError) {
        console.error("Error fetching bookings from API:", apiError);
        
        // Try to load from localStorage as a fallback
        try {
          const cachedBookingsJson = localStorage.getItem("enhancedUserBookings");
          if (cachedBookingsJson) {
            const cachedBookings = JSON.parse(cachedBookingsJson);
            if (Array.isArray(cachedBookings) && cachedBookings.length > 0) {
              console.log("Using cached bookings from localStorage:", cachedBookings.length);
              bookingsData = cachedBookings;
            } else {
              console.log("Cached bookings exist but are empty or invalid");
            }
          } else {
            console.log("No cached bookings found in localStorage");
          }
        } catch (cacheError) {
          console.error("Error reading cached bookings:", cacheError);
        }
        
        // If we still don't have any bookings and there's an error, show a friendly message
        if (bookingsData.length === 0) {
          setError("Unable to load your bookings. Please try again later.");
        }
      }
      
      setBookings(bookingsData);
      setLoading(false);
    } catch (err) {
      console.error("Error in loadBookings:", err);
      setError("An unexpected error occurred. Please try again later.");
      setBookings([]);
      setLoading(false);
    }
  }, []);

  // Hook to load bookings on component mount
  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Also reload bookings when agency status changes to approved
  useEffect(() => {
    if (agencyStatus === 'approved') {
      loadBookings();
    }
  }, [agencyStatus, loadBookings]);

  // Cancel a booking
  const handleCancelBooking = async (bookingId) => {
    try {
      setLoading(true);
      
      // Show immediate UI feedback by updating the booking state
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking._id === bookingId 
            ? { ...booking, isProcessing: true } 
            : booking
        )
      );
      
      setSuccessMessage("Processing cancellation request...");
      
      // Add debugging information
      console.log(`Attempting to cancel booking with ID: ${bookingId}`);
      
      // Get the current user data for debugging
      const userJson = localStorage.getItem("user");
      let userId = null;
      try {
        if (userJson) {
          const userData = JSON.parse(userJson);
          userId = userData._id;
          console.log(`User ${userId} is cancelling booking ${bookingId}`);
        }
      } catch (e) {
        console.error("Error getting user data during cancellation:", e);
      }
      
      // Call the API to cancel the booking
      await apiCancelBooking(bookingId);
      
      // Update the booking status in the UI
      setBookings(prev => prev.map(booking => 
        booking._id === bookingId 
          ? { ...booking, status: "cancelled", isProcessing: false, cancellationDate: new Date().toISOString() } 
          : booking
      ));
      
      // Update the cached bookings in localStorage
      try {
        const cachedBookingsJson = localStorage.getItem("enhancedUserBookings");
        if (cachedBookingsJson) {
          const cachedBookings = JSON.parse(cachedBookingsJson);
          const updatedCache = cachedBookings.map(booking => 
            booking._id === bookingId 
              ? { ...booking, status: "cancelled", cancellationDate: new Date().toISOString() } 
              : booking
          );
          localStorage.setItem("enhancedUserBookings", JSON.stringify(updatedCache));
          console.log("Updated cached bookings after cancellation");
        }
      } catch (cacheError) {
        console.warn("Failed to update cached bookings:", cacheError);
      }
      
      setSuccessMessage("Booking cancelled successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error cancelling booking:", error);
      
      // Restore the booking state
      setBookings(prev => prev.map(booking => 
        booking._id === bookingId 
          ? { ...booking, isProcessing: false } 
          : booking
      ));
      
      // Set appropriate error message
      if (error.response && error.response.status === 403) {
        setError("You don't have permission to cancel this booking. Please contact support.");
      } else if (error.response && error.response.status === 404) {
        setError("Booking not found. It may have already been cancelled or processed.");
      } else {
        setError(`Failed to cancel booking: ${error.message || "Unknown error"}`);
      }
      
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Handle profile form changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    console.log('Profile field change:', name, value);
    
    // Special handling for address field
    if (name === 'address') {
      setProfileData(prev => ({ 
        ...prev, 
        [name]: value // Don't trim here, allow spaces while typing
      }));
    } else {
      setProfileData(prev => ({ 
        ...prev, 
        [name]: value 
      }));
    }
  };

  // Handle profile changes
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Prepare the data for submission
      const updatedData = {
        name: profileData.name,
        phone: profileData.phone,
        address: profileData.address ? profileData.address.trim() : '' // Trim only when submitting
      };

      // Log the data being sent
      console.log('Sending profile update:', updatedData);

      const response = await axios.put('/api/auth/me', updatedData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const updatedUser = response.data;
      
      // Log the response
      console.log('Server response:', updatedUser);
      console.log('Server response address field specifically:', {
        address: updatedUser.address,
        addressType: typeof updatedUser.address,
        addressLength: updatedUser.address ? updatedUser.address.length : 'undefined'
      });
      
      // Update local storage - this might be redundant since updateUser() handles this
      // const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
      // const updatedUserData = {
      //   ...currentUserData,
      //   name: updatedUser.name,
      //   phone: updatedUser.phone,
      //   address: updatedUser.address
      // };
      // localStorage.setItem('user', JSON.stringify(updatedUserData));
      
      // Update the user data using the context's updateUser function
      const updateSuccess = await updateUser(updatedUser);
      
      if (!updateSuccess) {
        throw new Error('Failed to update user data in context');
      }

      // Update local state
      setProfileData(prev => ({
        ...prev,
        name: updatedUser.name || "",
        phone: updatedUser.phone || "",
        address: updatedUser.address || "",
      }));
      
      setIsEditingProfile(false);
      setSuccessMessage("Profile updated successfully");
      setTimeout(() => setSuccessMessage(""), 3000);

      // Don't call refreshUser() here as it might overwrite the data we just set
      // refreshUser();
    } catch (error) {
      console.error('Profile update error:', error);
      setError(error.response?.data?.message || error.message || "Failed to update profile");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Handle manual refresh button click
  const handleRefresh = () => {
    loadBookings();
    setSuccessMessage("Refreshing data...");
    setTimeout(() => setSuccessMessage(""), 2000);
  };

  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    
    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters long");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get the token
      const token = localStorage.getItem('token');
      
      // Use a more common API endpoint format - try the alternative endpoint
      const response = await axios.post(
        '/api/auth/change-password',
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Handle success
      setSuccessMessage("Password changed successfully");
      setShowPasswordModal(false);
      
      // Clear the form
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error changing password:", error);
      
      // Handle different error scenarios
      if (error.response) {
        if (error.response.status === 401) {
          setPasswordError("Current password is incorrect");
        } else if (error.response.status === 500) {
          setPasswordError("Server error. Please try again later or contact support.");
        } else {
          setPasswordError(error.response.data?.message || "Failed to change password");
        }
      } else if (error.request) {
        // The request was made but no response was received
        setPasswordError("No response from server. Please check your internet connection.");
      } else {
        // Something happened in setting up the request
        setPasswordError("Network error. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle tabs visibility for mobile
  const toggleTabs = () => {
    setTabsVisible(!tabsVisible);
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTabsVisible(false);
  };
  
  // Show booking details
  const showBookingDetails = (booking) => {
    setSelectedBooking(booking);
  };

  // Filter bookings based on status and search query
  const filteredBookings = bookings
    .filter(booking => {
      if (bookingFilter === "all") return true;
      return booking.status.toLowerCase() === bookingFilter.toLowerCase();
    })
    .filter(booking => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        (booking.lot?.name?.toLowerCase().includes(query)) ||
        (booking.lot?.phase?.toLowerCase().includes(query)) ||
        (booking.status?.toLowerCase().includes(query))
      );
    });

  // Calculate booking statistics
  const bookingStats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === "pending").length,
    approved: bookings.filter(b => b.status === "approved").length,
    cancelled: bookings.filter(b => b.status === "cancelled").length,
  };

  // Render booking status with appropriate styling
  const renderBookingStatus = (status) => {
    const statusClass = status.toLowerCase();
    return (
      <span className={`booking-status ${statusClass}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Render agency membership status
  const renderAgencyStatus = () => {
    if (!agencyStatus) return null;
    
    switch(agencyStatus) {
      case 'approved':
        return (
          <div className="agency-status-card approved">
            <h3>Agency Membership Active</h3>
            <p>You are a verified member.</p>
          </div>
        );
      case 'pending':
        return (
          <div className="agency-status-card pending">
            <h3>Agency Membership Pending</h3>
            <p>Your request to join <strong>{agencyName}</strong> is awaiting approval.</p>
            <p>You'll be notified once your request is processed.</p>
          </div>
        );
      case 'rejected':
        return (
          <div className="agency-status-card rejected">
            <h3>Agency Membership Rejected</h3>
            <p>Your request to join <strong>{agencyName}</strong> was not approved.</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/agency-request')}
            >
              Request Another Agency
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="loading-spinner">
      <div className="spinner"></div>
    </div>
  );

  // Empty state for bookings
  const EmptyBookingsState = () => (
    <div className="empty-state">
      <div className="empty-state-icon">📅</div>
      <h3 className="empty-state-title">No Bookings Found</h3>
      <p className="empty-state-text">You haven't made any bookings yet.</p>
      <button 
        className="btn btn-primary"
        onClick={() => navigate('/properties')}
      >
        Book Now
      </button>
    </div>
  );

  return (
    <div className="enhanced-user-dashboard">
      <div className="dashboard-header">
        <h2>
          Welcome, {currentUser && currentUser.name
            ? currentUser.name
            : "User"}
        </h2>
        
        <div className="header-actions">
          <button 
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh Data
          </button>
        </div>
      </div>
      
      {lastRefresh && (
        <div className="last-refresh">
          Last refreshed: {lastRefresh}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Dashboard Navigation Tabs */}
          <div className="dashboard-navigation">
            <div className="tab-selector">
              <button
                className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => handleTabChange('dashboard')}
              >
                Overview
              </button>
              <button
                className={`tab-btn ${activeTab === 'bookings' ? 'active' : ''}`}
                onClick={() => handleTabChange('bookings')}
              >
                My Bookings
              </button>
              <button
                className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => handleTabChange('profile')}
              >
                My Profile
              </button>
            </div>
          </div>

          {/* Main Content */}
          <main className="dashboard-content">
            {/* Dashboard Overview */}
            {activeTab === 'dashboard' && (
              <div className="fade-in">
                {/* Agency Membership Status */}
                {renderAgencyStatus()}
                
                {/* Only show the agency prompt if user isn't a member and doesn't have a pending request */}
                {!agencyStatus && !hasPendingAgencyRequest() && (
                  <div className="card agency-prompt-card">
                    <div className="card-header">
                      <h2 className="card-title">Join an Agency</h2>
                    </div>
                    <div className="card-body">
                      <p>Become a member of an agency to access exclusive industrial property listings.</p>
                      <button 
                        className="btn btn-primary"
                        onClick={() => navigate('/agency-request')}
                      >
                        Request Agency Membership
                      </button>
                    </div>
                  </div>
                )}

                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">Booking Overview</h2>
                  </div>
                  <div className="card-body">
                    <div className="stats-container">
                      <div className="stat-card total">
                        <div className="stat-value">{bookingStats.total}</div>
                        <p className="stat-label">Total Bookings</p>
                      </div>
                      <div className="stat-card pending">
                        <div className="stat-value">{bookingStats.pending}</div>
                        <p className="stat-label">Pending</p>
                      </div>
                      <div className="stat-card approved">
                        <div className="stat-value">{bookingStats.approved}</div>
                        <p className="stat-label">Approved</p>
                      </div>
                      <div className="stat-card cancelled">
                        <div className="stat-value">{bookingStats.cancelled}</div>
                        <p className="stat-label">Cancelled</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              <div className="fade-in">
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">My Bookings</h2>
                  </div>
                  <div className="card-body">
                    {/* Filter and Search */}
                    <div className="booking-actions-bar">
                      <div className="search-and-filter">
                        <div className="search-box">
                          <input
                            type="text"
                            className="search-input"
                            placeholder="Search bookings..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                          {searchQuery && (
                            <button 
                              className="clear-search-btn"
                              onClick={() => setSearchQuery('')}
                            >
                              ×
                            </button>
                          )}
                        </div>
                        <div className="filter-dropdown-container">
                          <select 
                            className="filter-dropdown" 
                            value={bookingFilter}
                            onChange={(e) => setBookingFilter(e.target.value)}
                          >
                            <option value="all">All Bookings ({bookingStats.total})</option>
                            <option value="pending">Pending ({bookingStats.pending})</option>
                            <option value="approved">Approved ({bookingStats.approved})</option>
                            <option value="cancelled">Cancelled ({bookingStats.cancelled})</option>
                          </select>
                        </div>
                      </div>
                      <button 
                        className="new-booking-btn"
                        onClick={() => navigate('/properties')}
                      >
                        + New Booking
                      </button>
                    </div>

                    {/* Bookings Table */}
                    {bookings.length === 0 ? (
                      <EmptyBookingsState />
                    ) : (
                      <div className="bookings-wrapper">
                        <table className="bookings-table">
                          <thead>
                            <tr>
                              <th>Lot #</th>
                              <th>Type</th>
                              <th>Creator</th>
                              <th>Status</th>
                              <th>Date</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBookings.map((booking) => (
                              <tr key={booking._id}>
                                <td data-label="Lot #">
                                  {booking.lot?.lotNumber || booking.lot?.name || 'N/A'}
                                </td>
                                <td data-label="Type">
                                  {booking.lot?.type || 'N/A'}
                                </td>
                                <td data-label="Creator">
                                  {booking.creatorEmail === currentUser?.email ? 'You' : booking.creatorName || 'Unknown'}
                                </td>
                                <td data-label="Status">
                                  {renderBookingStatus(booking.status)}
                                </td>
                                <td data-label="Date">
                                  <div className="date-time-info">
                                    <span className="date-display">{formatDate(booking.createdAt)}</span>
                                    <span className="time-display">{formatTime(booking.createdAt)}</span>
                                  </div>
                                </td>
                                <td data-label="Action">
                                  <div className="booking-actions">
                                    {booking.status !== 'cancelled' && 
                                     booking.status !== 'rejected' && 
                                     booking.creatorEmail === currentUser?.email && (
                                      <button 
                                        className="btn btn-danger btn-sm"
                                        onClick={() => handleCancelBooking(booking._id)}
                                      >
                                        Cancel
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="fade-in">
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">My Profile</h2>
                    {!isEditingProfile && (
                      <button 
                        className="btn btn-primary"
                        onClick={() => setIsEditingProfile(true)}
                      >
                        Edit Profile
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    {isEditingProfile ? (
                      <form onSubmit={handleSaveProfile}>
                        <div className="form-group">
                          <label htmlFor="name">Full Name</label>
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            name="name"
                            value={profileData.name}
                            onChange={handleProfileChange}
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="email">Email Address</label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="email"
                            value={profileData.email}
                            onChange={handleProfileChange}
                            disabled
                          />
                          <small className="text-muted">Email cannot be changed</small>
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="phone">Phone Number</label>
                          <input
                            type="tel"
                            className="form-control"
                            id="phone"
                            name="phone"
                            value={profileData.phone}
                            onChange={handleProfileChange}
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="address">Address</label>
                          <textarea
                            className="form-control"
                            id="address"
                            name="address"
                            value={profileData.address || ''}
                            onChange={handleProfileChange}
                            rows="3"
                          ></textarea>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                          <button type="submit" className="btn btn-success">
                            Save Changes
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={() => {
                              setIsEditingProfile(false);
                              // Reset form data to current user data
                              setProfileData({
                                name: currentUser?.name || "",
                                email: currentUser?.email || "",
                                phone: currentUser?.phone || "",
                                address: currentUser?.address || "",
                              });
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="profile-info">
                        <div className="profile-detail">
                          <span className="profile-label">Full Name</span>
                          <div className="profile-value">{currentUser?.name || 'Not provided'}</div>
                        </div>
                        
                        <div className="profile-detail">
                          <span className="profile-label">Email Address</span>
                          <div className="profile-value">{currentUser?.email || 'Not provided'}</div>
                        </div>
                        
                        <div className="profile-detail">
                          <span className="profile-label">Phone Number</span>
                          <div className="profile-value">{currentUser?.phone || 'Not provided'}</div>
                        </div>
                        
                        <div className="profile-detail">
                          <span className="profile-label">Address</span>
                          <div className="profile-value">{currentUser?.address || 'Not provided'}</div>
                        </div>
                        
                        <div className="profile-detail">
                          <span className="profile-label">Account Type</span>
                          <div className="profile-value">
                            {currentUser?.role === 'super_admin' ? 'Administrator' : 
                             currentUser?.role === 'agent_admin' ? 'Agent' : 'Regular User'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">Account Settings</h2>
                  </div>
                  <div className="card-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <div>
                        <h3 style={{ fontSize: '1.1rem', margin: '0 0 0.5rem 0' }}>Password</h3>
                        <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
                          Change your account password to maintain security
                        </p>
                        <button 
                          className="btn btn-primary"
                          onClick={() => {
                            setShowPasswordModal(true);
                            setPasswordData({
                              currentPassword: "",
                              newPassword: "",
                              confirmPassword: ""
                            });
                            setPasswordError("");
                          }}
                        >
                          Change Password
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
          
          {/* Booking Details Modal */}
          {selectedBooking && (
            <div className="modal-backdrop">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Booking Details</h3>
                  <button className="close-button" onClick={() => setSelectedBooking(null)}>×</button>
                </div>
                
                <div className="modal-body">
                  <div className="booking-details">
                    <div className="profile-detail">
                      <span className="profile-label">Booking ID</span>
                      <div className="profile-value">{selectedBooking._id}</div>
                    </div>
                    
                    <div className="profile-detail">
                      <span className="profile-label">Property</span>
                      <div className="profile-value">
                        {selectedBooking.lot?.name || 
                         selectedBooking.unit?.name || 
                         (selectedBooking.specialLotType && `Type ${selectedBooking.specialLotType} ${selectedBooking.specialLotNumber}`) || 
                         'N/A'}
                      </div>
                    </div>
                    
                    <div className="profile-detail">
                      <span className="profile-label">Phase</span>
                      <div className="profile-value">{selectedBooking.lot?.phase || 'N/A'}</div>
                    </div>
                    
                    <div className="profile-detail">
                      <span className="profile-label">Date Requested</span>
                      <div className="profile-value">
                        {formatDate(selectedBooking.createdAt)} at {formatTime(selectedBooking.createdAt)}
                      </div>
                    </div>
                    
                    <div className="profile-detail">
                      <span className="profile-label">Status</span>
                      <div className="profile-value">
                        {renderBookingStatus(selectedBooking.status)}
                      </div>
                    </div>
                    
                    {selectedBooking.requestMessage && (
                      <div className="profile-detail">
                        <span className="profile-label">Request Message</span>
                        <div className="profile-value">{selectedBooking.requestMessage}</div>
                      </div>
                    )}
                    
                    {selectedBooking.adminNotes && (
                      <div className="profile-detail">
                        <span className="profile-label">Admin Notes</span>
                        <div className="profile-value">{selectedBooking.adminNotes}</div>
                      </div>
                    )}
                    
                    {selectedBooking.agentNotes && (
                      <div className="profile-detail">
                        <span className="profile-label">Agent Notes</span>
                        <div className="profile-value">{selectedBooking.agentNotes}</div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    className="btn btn-primary" 
                    onClick={() => setSelectedBooking(null)}
                  >
                    Close
                  </button>
                  
                  {selectedBooking.status === "pending" && (
                    <button 
                      className="btn btn-danger" 
                      onClick={() => {
                        handleCancelBooking(selectedBooking._id);
                        setSelectedBooking(null);
                      }}
                    >
                      Cancel Booking
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Password Change Modal */}
          {showPasswordModal && (
            <div className="modal-backdrop">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Change Password</h3>
                  <button className="close-button" onClick={() => setShowPasswordModal(false)}>×</button>
                </div>
                
                <div className="modal-body">
                  {passwordError && (
                    <div className="error-message" style={{ marginBottom: '15px' }}>
                      {passwordError}
                    </div>
                  )}
                  
                  <form onSubmit={handleChangePassword}>
                    <div className="form-group">
                      <label htmlFor="currentPassword">Current Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        required
                        disabled={isSubmitting}
                        autoComplete="current-password"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="newPassword">New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        required
                        disabled={isSubmitting}
                        minLength={6}
                        autoComplete="new-password"
                      />
                      <small className="text-muted">Password must be at least 6 characters long</small>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="confirmPassword">Confirm Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        required
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                      <button type="submit" className="btn btn-success" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span className="ms-2">Processing...</span>
                          </>
                        ) : (
                          'Change Password'
                        )}
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => setShowPasswordModal(false)}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EnhancedUserDashboard; 